import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AnalyticsApiService from '../services/analyticsApi';
import { toast } from 'sonner';

interface BoostKind {
  boost_kind_id: number;
  name: string;
  description?: string;
}

interface UserBoost {
  boost_kind_id: number;
  boost_name: string;
  boost_description?: string;
  created_at: string;
  updated_at: string;
}

interface BoostModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  userName: string;
}

export function BoostModal({ isOpen, onClose, userId, userName }: BoostModalProps) {
  const [boostKinds, setBoostKinds] = useState<BoostKind[]>([]);
  const [currentBoost, setCurrentBoost] = useState<UserBoost | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedBoostId, setSelectedBoostId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, userId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [kindsResponse, boostResponse] = await Promise.all([
        AnalyticsApiService.getBoostKinds(),
        AnalyticsApiService.getUserBoost(userId)
      ]);
      
      setBoostKinds(kindsResponse.boost_kinds);
      setCurrentBoost(boostResponse.boost);
      setSelectedBoostId(boostResponse.boost?.boost_kind_id || null);
    } catch (error) {
      console.error('Error loading boost data:', error);
      toast.error('Failed to load boost data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyBoost = async () => {
    if (!selectedBoostId) {
      toast.error('Please select a boost type');
      return;
    }

    setSaving(true);
    try {
      await AnalyticsApiService.addOrUpdateBoost(userId, selectedBoostId);
      await loadData(); // Reload to get updated boost
      
      const boostName = boostKinds.find(b => b.boost_kind_id === selectedBoostId)?.name;
      toast.success(`Applied "${boostName}" boost to ${userName}`);
    } catch (error: any) {
      console.error('Error applying boost:', error);
      toast.error(error.message || 'Failed to apply boost');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveBoost = async () => {
    setSaving(true);
    try {
      await AnalyticsApiService.removeBoost(userId);
      setCurrentBoost(null);
      setSelectedBoostId(null);
      toast.success(`Removed boost from ${userName}`);
    } catch (error: any) {
      console.error('Error removing boost:', error);
      toast.error(error.message || 'Failed to remove boost');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const hasChanges = selectedBoostId !== (currentBoost?.boost_kind_id || null);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', duration: 0.3 }}
          className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                ⚡ Boost User
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {userName} (User ID: {userId})
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-gray-600 dark:border-t-gray-300 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Current Boost Status */}
                {currentBoost && (
                  <div className="mb-4 p-4 bg-green-50 dark:bg-green-950/30 border-2 border-green-500 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-900 dark:text-green-100">
                          Current Boost: {currentBoost.boost_name}
                        </div>
                        <div className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                          Applied: {new Date(currentBoost.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Boost Options */}
                {boostKinds.map(boostKind => {
                  const isSelected = selectedBoostId === boostKind.boost_kind_id;

                  return (
                    <label
                      key={boostKind.boost_kind_id}
                      className={`
                        flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all
                        ${isSelected 
                          ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }
                        ${saving ? 'opacity-50 cursor-wait' : ''}
                      `}
                    >
                      <input
                        type="radio"
                        name="boost"
                        checked={isSelected}
                        onChange={() => setSelectedBoostId(boostKind.boost_kind_id)}
                        disabled={saving}
                        className="mt-1 w-5 h-5 text-yellow-600 focus:ring-yellow-500 cursor-pointer"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {boostKind.name}
                        </div>
                        {boostKind.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {boostKind.description}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex gap-2">
              {currentBoost && (
                <button
                  onClick={handleRemoveBoost}
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
                >
                  {saving ? 'Removing...' : 'Remove Boost'}
                </button>
              )}
              <button
                onClick={handleApplyBoost}
                disabled={saving || !hasChanges}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
              >
                {saving ? 'Applying...' : hasChanges ? 'Apply Boost' : 'No Changes'}
              </button>
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

